import { ApiError, listAuditEntries, type AuditEntry } from "@/api";
import { formatDateTime } from "@/lib/format";
import { useEffect, useState } from "react";

export function AuditPage() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subjectType, setSubjectType] = useState("");
  const [action, setAction] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  function load() {
    listAuditEntries({
      subject_type: subjectType.trim() || undefined,
      action: action.trim() || undefined,
    })
      .then((items) => {
        setEntries(items);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Audit-Log konnte nicht geladen werden.");
      });
  }

  useEffect(() => {
    load();
  }, [subjectType, action]);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-4">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-base font-semibold">Audit-Log</h1>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          value={subjectType}
          onChange={(event) => setSubjectType(event.target.value)}
          placeholder="Subjekt-Typ (z. B. message)"
          className="w-56 rounded-md border border-hairline bg-surface px-2 py-1 text-sm"
        />
        <input
          value={action}
          onChange={(event) => setAction(event.target.value)}
          placeholder="Aktion (z. B. message.sent)"
          className="w-56 rounded-md border border-hairline bg-surface px-2 py-1 text-sm"
        />
      </div>

      {error ? <p className="mb-3 text-sm text-danger">{error}</p> : null}
      {entries === null && !error ? (
        <div className="space-y-2">
          <div className="h-10 animate-pulse rounded-md bg-surface-subtle" />
          <div className="h-10 animate-pulse rounded-md bg-surface-subtle" />
        </div>
      ) : null}
      {entries && entries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-hairline bg-surface px-6 py-12 text-center text-sm text-muted">
          Keine Einträge.
        </div>
      ) : null}
      {entries && entries.length > 0 ? (
        <ul className="divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-surface">
          {entries.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface-subtle"
              >
                <span className="w-40 shrink-0 text-[11px] text-muted tabular-nums">
                  {formatDateTime(entry.at)}
                </span>
                <span className="w-44 shrink-0 truncate text-sm font-medium">{entry.action}</span>
                <span className="min-w-0 flex-1 truncate text-[11px] text-muted">
                  {typeof entry.detail?.subject === "string" && entry.detail.subject
                    ? entry.detail.subject
                    : `${entry.subject_type} · ${entry.subject_id}`}
                </span>
                <span className="shrink-0 text-[11px] text-muted">
                  {entry.actor_display_name ?? entry.actor_id}
                </span>
              </button>
              {expanded === entry.id && entry.detail ? (
                <pre className="overflow-x-auto bg-surface-subtle px-3 py-2 text-[11px] text-muted">
                  {JSON.stringify(entry.detail, null, 2)}
                </pre>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
